import { useState, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiBrokerUploadDocument,
  apiResolveDocument,
  apiIgnoreDocument,
  apiDeleteUploadedFile,
} from 'api';
import { DeferredDocument } from 'types/documents';

interface UseDocumentOperationsProps {
  policyUuid: string;
  onValidationStart?: (docKey: string, validationLogId: number) => void;
  onValidationClear?: (docKey: string) => void;
}

export const useDocumentOperations = ({
  policyUuid,
  onValidationStart,
  onValidationClear,
}: UseDocumentOperationsProps) => {
  const [uploadingDocKey, setUploadingDocKey] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const toast = useToast();
  const queryClient = useQueryClient();

  const handleUploadClick = (docKey: string) => {
    const fileInput = fileInputRefs.current[docKey];
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    doc: DeferredDocument,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const docKey = `${doc.validation_key}-${doc.item_key}`;
    setUploadingDocKey(docKey);

    try {
      const response = await apiBrokerUploadDocument(
        policyUuid,
        doc.validation_key,
        doc.item_key,
        file,
        doc.type,
      );

      toast({
        title: 'File uploaded successfully',
        description: `Document "${doc.text}" has been uploaded`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // If validation_log_id is present, start tracking validation
      if (response.validation_log_id && onValidationStart) {
        onValidationStart(docKey, response.validation_log_id);

        toast({
          title: 'Validation started',
          description: 'Your document is being validated...',
          status: 'info',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      }

      // Refetch the deferred documents list to update the UI
      queryClient.invalidateQueries({ queryKey: ['deferredDocuments'] });

      // Reset file input
      event.target.value = '';
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: 'Failed to upload the document. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setUploadingDocKey(null);
    }
  };

  const handleResolve = async (doc: DeferredDocument) => {
    setIsProcessing(true);
    try {
      await apiResolveDocument(policyUuid, doc.validation_key, doc.item_key);

      toast({
        title: 'Document resolved',
        description: `"${doc.text}" has been marked as resolved`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Refetch the deferred documents list
      queryClient.invalidateQueries({ queryKey: ['deferredDocuments'] });

      return true;
    } catch (error) {
      console.error('Failed to resolve document:', error);
      toast({
        title: 'Failed to resolve',
        description:
          'Failed to mark the document as resolved. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIgnore = async (doc: DeferredDocument) => {
    setIsProcessing(true);
    try {
      await apiIgnoreDocument(policyUuid, doc.validation_key, doc.item_key);

      toast({
        title: 'Document ignored',
        description: `"${doc.text}" has been marked as cannot obtain`,
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Refetch the deferred documents list
      queryClient.invalidateQueries({ queryKey: ['deferredDocuments'] });

      return true;
    } catch (error) {
      console.error('Failed to ignore document:', error);
      toast({
        title: 'Failed to ignore',
        description:
          'Failed to mark the document as ignored. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteFile = async (doc: DeferredDocument, fileId: number) => {
    const docKey = `${doc.validation_key}-${doc.item_key}`;

    try {
      await apiDeleteUploadedFile(
        policyUuid,
        doc.validation_key,
        doc.item_key,
        fileId,
      );

      // Clear validation state for this document (backend deletes validation logs)
      if (onValidationClear) {
        onValidationClear(docKey);
      }

      toast({
        title: 'File deleted',
        description: 'The uploaded file has been removed',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Refetch the deferred documents list
      queryClient.invalidateQueries({ queryKey: ['deferredDocuments'] });

      return true;
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast({
        title: 'Delete failed',
        description: 'Failed to delete the file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }
  };

  return {
    uploadingDocKey,
    isProcessing,
    fileInputRefs,
    handleUploadClick,
    handleFileChange,
    handleResolve,
    handleIgnore,
    handleDeleteFile,
  };
};
